<script lang="ts">
  import { getStoresContext } from "~/stores";
  import { getNavigationUrlsFromRouter } from "~/theme/demo2025/stores/navigation-utils";
  import type { StoresKey } from "~/stores";
  import type { ManageAPI } from "~/manage-api/types";
  import StoreSearchResult from "../result-items/StoreSearchResult.svelte";
  import OfferSearchResult from "../result-items/OfferSearchResult.svelte";
  import EventSearchResult from "../result-items/EventSearchResult.svelte";
  
  export let request_store_key: StoresKey;
  export let results: {
    stores: ManageAPI.Store[];
    offers: ManageAPI.Offer[];
    events: ManageAPI.Event[];
  };
  
  // Badge styling properties - can be overridden at component level
  export let badgeBackgroundColor: string = "";
  export let badgeTextColor: string = "";
  export let badgeBorderRadius: string = "";

  // Search results overlay styling
  export let backgroundColor: string = "#fff";
  export let textColor: string = "#424242";
  export let sectionHeaderBorderColor: string = "#EFEFEF";
  export let sectionHeaderBorderThickness: string = "1px";

  const { T, locale, reverse_router } = getStoresContext(request_store_key);
  const urls = getNavigationUrlsFromRouter(reverse_router, $locale);

  $: t = $T;

  let scrolled = false;
  
  function handleScroll(e: Event) {
    const target = e.target as HTMLElement;
    scrolled = target.scrollTop > 0;
  }

  $: hasResults = results.stores.length > 0 || 
                 results.offers.length > 0 || 
                 results.events.length > 0;
</script>

<div class="search-results" style="--search-results-bg: {backgroundColor}; --search-results-text: {textColor};">
  <div class="search-results-scroll" on:scroll={handleScroll}>
    {#if results.stores.length > 0}
      <section class="results-section">
        <a href={urls.stores} class="section-header" class:scrolled>
          <span class="title">{t('common.stores')}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.5 14.5L14.5 5.5M14.5 5.5H6.5M14.5 5.5V13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
        <ul>
          {#each results.stores as store}
            <StoreSearchResult 
              {store} 
              {request_store_key} 
              {badgeBackgroundColor}
              {badgeTextColor}
              {badgeBorderRadius}
            />
          {/each}
        </ul>
      </section>
    {/if}

    {#if results.offers.length > 0}
      <section class="results-section">
        <a href={urls.offers} class="section-header" class:scrolled>
          <span class="title">{t('common.offers')}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.5 14.5L14.5 5.5M14.5 5.5H6.5M14.5 5.5V13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
        <ul>
          {#each results.offers as offer}
            <OfferSearchResult 
              {offer} 
              {request_store_key} 
              {badgeBackgroundColor}
              {badgeTextColor}
              {badgeBorderRadius}
            />
          {/each}
        </ul>
      </section>
    {/if}

    {#if results.events.length > 0}
      <section class="results-section">
        <a href={urls.events} class="section-header" class:scrolled>
          <span class="title">{t('common.events')}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.5 14.5L14.5 5.5M14.5 5.5H6.5M14.5 5.5V13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
        <ul>
          {#each results.events as event}
            <EventSearchResult 
              {event} 
              {request_store_key} 
              {badgeBackgroundColor}
              {badgeTextColor}
              {badgeBorderRadius}
            />
          {/each}
        </ul>
      </section>
    {/if}

    {#if !hasResults}
      <div class="no-results">
        <p>{t('search.no-results')}</p>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--search-results-bg, white);
  color: var(--search-results-text, #424242);
  border-radius: 0 0 8px 8px;
  z-index: 100;
  overflow: hidden;
  margin-left: -1rem;
  margin-right: -1rem;
}

.search-results-scroll {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 1rem;
  --search-results-scope: true;
}

.search-results-scroll :global(.store-result),
.search-results-scroll :global(.offer-result),
.search-results-scroll :global(.event-result) {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  text-decoration: none;
  border-radius: 4px;
  background: var(--search-results-bg, white);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform, box-shadow;
  color: var(--search-results-text, #424242);
}

.search-results-scroll :global(.store-result:hover),
.search-results-scroll :global(.offer-result:hover),
.search-results-scroll :global(.event-result:hover) {
  transform: translateY(-2px) scale(1.01);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  text-decoration: none;
}

.search-results-scroll :global(.store-result a),
.search-results-scroll :global(.offer-result a),
.search-results-scroll :global(.event-result a),
.search-results-scroll :global(.store-result a:hover),
.search-results-scroll :global(.offer-result a:hover),
.search-results-scroll :global(.event-result a:hover) {
  color: var(--search-results-text, #424242);
  text-decoration: none;
}

.results-section {
  position: relative;
  padding-bottom: 1rem;
}

.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--search-results-bg, white);
  color: var(--search-results-text, #424242);
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
  margin-left: -1rem;
  margin-right: -1rem;
  padding: 1rem 1rem;
  border-bottom: var(--search-results-section-header-border-thickness, 1px) solid var(--search-results-section-header-border-color, #EFEFEF);

  @media (max-width: 991px) {
    margin: 0;
  }

  &.scrolled {
    border-bottom-color: var(--search-results-section-header-border-color, #EFEFEF);
    border-bottom-width: var(--search-results-section-header-border-thickness, 1px);
  }

  &:hover {
    svg {
      transform: translate(4px, -4px);
    }
  }

  svg {
    transition: transform 0.2s ease;
    color: var(--search-results-text, #424242);
  }
}

.title {
  font-family: var(--liferay-font-family-sans-serif);
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--search-results-text, #424242);
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.no-results {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: var(--search-results-text, #424242);
  font-family: var(--liferay-font-family-base);
  font-size: 14px;
  background: var(--search-results-bg, white);
  border-radius: 4px;
  min-height: 120px;
}
</style> 